import React from "react";
import PropTypes from 'prop-types';
import classnames from "classnames";

// Components
import { Link, NavLink } from "react-router-dom";
import { Spinner } from "components/core/spinner";

// Definitions
export const sizes =   {
  xs: 'px-2.5 py-1 text-xs rounded-md',
  sm: 'px-3 py-2 text-sm rounded-md',
  smWide: 'px-6 py-2 text-sm rounded-md',
  md: 'px-4 py-2 text-sm rounded-md',
  md2: 'px-2 py-0.5 text-sm rounded-md',
  mdWide: 'px-8 py-2 text-sm rounded-md',
  lg: 'px-4 py-2 text-base rounded-md',
  xl: 'px-6 py-3 text-base rounded-md',
  nextToForm: 'px-6 py-3 text-base rounded-md',
  icon: 'text-xl rounded',
  link: 'text-base',
  sphere: 'w-10 h-10 text-center rounded-full ',
  toolbarIcon: 'text-xl p-2 rounded-md',
  smToolbarIcon: 'text-lg p-1',
  nav: 'h-full px-4 py-2 text-base',
  sideNav: 'px-4 py-2 md:px-1 md:py-1 2xl:px-4 2xl:py-2 md:text-xl 2xl:text-base whitespace-nowrap w-full flex items-center rounded-md',
  subSideNav: 'px-2 py-2 text-base whitespace-nowrap w-full flex items-center',
  flag: 'px-1 py-1 rounded-md text-base whitespace-nowrap w-full flex items-center'
};

export const colors =   {
  contrastWhite: 'shadow border-0 bg-white text-orange-600 hover:text-orange-800 hover:bg-gray-200 transition-all duration-100 outline-none ',
  contrastWhiteGray: 'shadow border-0 bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-200 transition-all duration-100 outline-none ',
  contrastWhiteGray2: 'shadow-sm border border-slate-300 bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-100 transition-all duration-100 outline-none ',
  contrastWhiteGrayInactive: 'opacity-40 shadow border-0 bg-white text-gray-600 hover:text-gray-800 hover:bg-gray-200 transition-all duration-100 outline-none ',
  cardNav: 'font-medium border-0 text-orange-600 hover:text-orange-800 hover:bg-gray-100  transition-all duration-100 outline-none',
  warning: 'shadow-sm border-0 bg-yellow-100 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-200 transition-all duration-100 outline-none ',
  default: 'shadow-none border-0 bg-gray-100 text-gray-600 hover:text-gray-800 hover:bg-gray-200 transition-all duration-100 outline-none ',
  outline: 'shadow-none border-0 bg-none text-gray-600 hover:text-gray-800 hover:bg-gray-200 transition-all duration-100 outline-none ',
  active: 'shadow-none border-0 text-white bg-orange-600 hover:text-white hover:bg-orange-500 outline-none ',
  gray: 'shadow-none border-0 text-gray-600 bg-gray-100 hover:text-gray-700 hover:bg-gray-50 outline-none ',
  darkGray: 'shadow-none border-0 text-gray-100 bg-gray-600 hover:bg-gray-700 outline-none ',
  green: 'shadow-none border-0 text-green-600 bg-green-100 hover:text-green-700 hover:bg-green-50 outline-none ',
  toolbarIcon: "text-orange-600 hover:text-orange-700 hover:bg-orange-100 rounded-md",
  delete: 'shadow-none border-0 text-red-600 hover:text-red-700 hover:bg-red-50 outline-none ',
  deleteContrast: 'shadow-none border-0 bg-red-500 text-white hover:bg-red-600  outline-none ',
  black: 'flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gray-800 hover:bg-gray-600',
  paginate: 'border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50',
  inlineLink: 'text-orange-500 shadow-none border-0 hover:underline',
  baseLink: 'text-gray-500 shadow-none border-0 hover:text-orange-500 hover:underline',
  nav: "text-gray-500 font-medium",
  activeNav: "text-gray-900 hover:text-gray-700 font-medium border-b border-gray-500",
  sideNav: "text-gray-300 hover:text-gray-200 hover:bg-gray-700 font-medium",
  activeSideNav: "text-white hover:text-orange-50 font-medium bg-orange-600 md:border-r-2 md:border-r-orange-600 2xl:border-0",
  subSideNav: "text-gray-500 rounded-md font-medium bg-white hover:text-gray-800 hover:bg-gray-50",
  activeSubSideNav: "text-gray-900 font-medium rounded-md bg-gray-100",
  cell: "bg-white hover:bg-gray-200",
  activeCell: "bg-orange-500 hover:bg-orange-500",
  activeFlag: "bg-red-400 text-white hover:bg-red-400",
  flag: "bg-gray-50 text-gray-300 hover:bg-gray-100 hover:text-gray-600",
  none: ""
};

const layouts =   {
  block: 'w-full flex items-center justify-center',
  blockLeft: 'w-full flex items-center justify-left',
  cell: 'w-full block',
  smBlock: 'w-full flex justify-center sm:inline-flex sm:items-center sm:w-auto',
  lgBlock: 'w-full flex justify-center lg:inline-flex lg:items-center lg:w-auto',
  base: 'inline-flex items-center',
  none: ''
};

export function Button({ block, layout, color, size, children, disabled, loading, className, nav, href, to, ...props }){
  var _className = classnames("border-0 z-0",
                            sizes[size],
                            block? layouts.block: layout? layouts[layout]:layouts.base,
                            colors[color],
                            disabled? 'opacity-30 cursor-default': '',
                            className)
  if (href){
    return <a href={href} target={"_blank"} rel="noreferrer" className={_className} disabled={disabled || loading} {...props}>
          {loading? <Spinner size={'sm'}/> : children }
          </a>
  }
  if (to){
    if (nav){
        const cn = ({isActive}) => classnames("border-0 z-0",
                                    sizes[size],
                                    block? layouts.block: layout? layouts[layout]:layouts.base,
                                    colors[color(isActive)],
                                    disabled? 'opacity-30 cursor-default': '',
                                    className)
        return <NavLink to={to} className={cn} disabled={disabled || loading} {...props}>
              {children}
          </NavLink>
    }
    return <Link to={to} className={_className} disabled={disabled || loading} {...props}>
          {loading? <Spinner size={'sm'}/> : children }
          </Link>
  }
  return <button className={_className} disabled={disabled || loading} {...props}>
          {loading? <Spinner size={'sm'} className={"text-white"} /> : children }
          </button>
}

Button.propTypes = {
  block: PropTypes.bool, // block, base
  size: PropTypes.string, // xs, sm, md, lg, xl, 2xl
  loading: PropTypes.bool,
  className: PropTypes.string,
}
